import { useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';
import { parseFontVariant } from '@air/utils-fonts';

import { getFontFamilyCssValue } from '~/hooks/useFontLoader';

export type PublicHeadlineFont = {
  fontFamily: string;
  /** Font weight the loader requests for a built-in (Google Fonts) family; absent → 400;700. */
  variant?: string;
  /**
   * The `variant`'s weight, for headline surfaces to APPLY. Absent (with `fontStyle`) whenever the
   * variant is — the loader then requests 400;700 and each surface keeps the weight it hardcoded
   * before this existed, via its own CSS var fallback. When present it necessarily matches the one
   * face the loader requested, which is the point: a surface can no longer ask for a weight the
   * document never loaded, so the browser has nothing to synthesize.
   */
  fontWeight?: number;
  /** The `variant`'s style, for headline surfaces to APPLY; absent alongside `fontWeight`. */
  fontStyle?: 'italic' | 'normal';
  /**
   * Uploaded font file the page must register itself via `@font-face` — set for `asset` fonts
   * always, and for `contextSource` fonts that resolved to an upload. A `contextSource` resolving
   * to a built-in family arrives with `fontFamily` and no url, so it loads like a system font.
   */
  fontFaceUrl?: string;
};

/** The CSS `font-family` value every headline consumer shares — quoted family + sans-serif fallback. */
export const getPublicHeadlineFontFamilyValue = (fontFamily: string): string => getFontFamilyCssValue(fontFamily);

/**
 * The share link's custom headline font, when the publisher set one. Applied to headline elements
 * only (page title, gallery board titles) on public `collection` and `board` share pages; body
 * text and asset filenames stay in the system font. Returns `undefined` when unset so consumers fall through to
 * their default rendering. Must be called within a `ShortIdProvider`.
 */
export const usePublicHeadlineFont = (): PublicHeadlineFont | undefined => {
  const { presentationSettings } = useShortIdContext();
  const headlineFont = presentationSettings?.customFonts?.headlineFont;

  return useMemo(() => {
    // The `fontFamily` guard is a runtime-only escape hatch: the editor preview can feed a draft
    // whose `contextSource` value hasn't been server-resolved yet (write shape — no fontFamily);
    // there is nothing to render until resolution.
    if (!headlineFont || !headlineFont.fontFamily) {
      return undefined;
    }

    // Same parse the loader runs on the same variant, so the published weight/style are exactly the
    // face it requested. No variant → nothing to publish (surfaces fall back to their own weights).
    const { fontStyle, fontWeight } = parseFontVariant(headlineFont.variant);

    return {
      fontFamily: headlineFont.fontFamily,
      variant: headlineFont.variant,
      fontWeight: headlineFont.variant ? fontWeight : undefined,
      fontStyle: headlineFont.variant ? fontStyle : undefined,
      fontFaceUrl: headlineFont.type === 'system' ? undefined : headlineFont.url,
    };
  }, [headlineFont]);
};
